import { z } from 'zod';

/**
 * Méta-schéma du Form Builder (US-ADM-08).
 *
 * Un FormSchema est un document JSON décrivant un formulaire dynamique :
 *  - associé à une entité cible (`user`, `equipement`, etc.) + sous-type optionnel
 *  - composé d'une liste ordonnée de champs (`FormField`)
 *  - les champs sont stockés dans `<entity>.custom_fields` (JSONB) à l'instance
 *
 * Le frontend l'utilise pour rendre dynamiquement les formulaires de
 * création/édition. Le backend l'utilise pour valider les `custom_fields`
 * soumis (via `validateCustomFields`).
 */

/** Types de champs supportés (US-ADM-08). */
export const FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'date',
  'datetime',
  'boolean',
  'select',
  'multiselect',
  'radio',
  'email',
  'phone',
  'url',
  'json',
  'file',
  'section_title',
  'separator',
] as const;

export type FormFieldType = (typeof FIELD_TYPES)[number];

/** Entités cibles d'un schéma (US-ADM-08). */
export const FORM_SCHEMA_ENTITY_TYPES = [
  'user',
  'batiment',
  'etage',
  'salle',
  'service',
  'equipement',
  'intervention',
  'reservation',
  'maintenance',
  'etalonnage',
  'document',
] as const;

export type FormSchemaEntityType = (typeof FORM_SCHEMA_ENTITY_TYPES)[number];

/**
 * Option d'un champ select/multiselect/radio.
 */
export const formFieldOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
});

export type FormFieldOption = z.infer<typeof formFieldOptionSchema>;

/**
 * Règles de validation par champ (toutes optionnelles).
 * - `min`/`max` : pour number, length pour text/textarea
 * - `regex` : pattern à matcher (text/email/phone/url)
 * - `accept` : MIME types pour file (ex. "image/*,application/pdf")
 * - `max_size_mb` : taille max d'un fichier
 */
export const formFieldValidationSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
  regex: z.string().optional(),
  accept: z.string().optional(),
  max_size_mb: z.number().positive().optional(),
});

export type FormFieldValidation = z.infer<typeof formFieldValidationSchema>;

/**
 * Condition d'affichage : le champ n'est visible que si `field` (autre champ
 * du même schéma) prend la valeur `equals`.
 */
export const formFieldConditionSchema = z.object({
  field: z.string().min(1),
  equals: z.unknown(),
});

export type FormFieldCondition = z.infer<typeof formFieldConditionSchema>;

/**
 * Définition d'un champ unique.
 * - `key` : clé technique unique dans le schéma (ex. "telephone", "service_id")
 *   utilisée comme clé JSONB dans `<entity>.custom_fields`.
 * - `label` : libellé affiché à l'utilisateur.
 */
export const formFieldSchema = z.object({
  key: z
    .string()
    .min(1, 'Clé requise')
    .max(80)
    .regex(/^[a-z][a-z0-9_]*$/, 'snake_case minuscule, ne commence pas par un chiffre'),
  label: z.string().min(1).max(255),
  type: z.enum(FIELD_TYPES),
  required: z.boolean().default(false),
  placeholder: z.string().max(255).optional(),
  help: z.string().max(500).optional(),
  default: z.unknown().optional(),
  options: z.array(formFieldOptionSchema).optional(),
  validation: formFieldValidationSchema.optional(),
  visible_if: formFieldConditionSchema.optional(),
});

export type FormField = z.infer<typeof formFieldSchema>;

/**
 * Document de schéma de formulaire (la valeur stockée dans
 * `form_schemas.schema` JSONB).
 */
export const formSchemaDocumentSchema = z.object({
  fields: z.array(formFieldSchema).min(0),
});

export type FormSchemaDocument = z.infer<typeof formSchemaDocumentSchema>;

/**
 * Schéma de création d'un FormSchema (route POST /admin/form-schemas).
 */
export const createFormSchemaSchema = z.object({
  entity_type: z.enum(FORM_SCHEMA_ENTITY_TYPES),
  entity_subtype: z.string().max(100).nullable().optional(),
  name: z.string().min(2).max(255),
  description: z.string().max(2000).nullable().optional(),
  schema: formSchemaDocumentSchema,
  is_active: z.boolean().default(false),
});

export type CreateFormSchemaInput = z.infer<typeof createFormSchemaSchema>;

export const updateFormSchemaSchema = z.object({
  name: z.string().min(2).max(255).optional(),
  description: z.string().max(2000).nullable().optional(),
  schema: formSchemaDocumentSchema.optional(),
});

export type UpdateFormSchemaInput = z.infer<typeof updateFormSchemaSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Validation des valeurs (custom_fields) contre un schéma
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Erreur de validation par champ — clé du champ → liste de messages.
 */
export type FormValidationErrors = Record<string, string[]>;

/**
 * Valide un objet de valeurs (`custom_fields`) contre un FormSchema.
 *
 * Retourne `{ valid: true, values }` si OK, où `values` est l'objet
 * éventuellement coercé (parsing booléens/nombres/dates, défauts appliqués).
 *
 * Retourne `{ valid: false, errors }` sinon — pour usage côté backend
 * (renvoyer 400) ou côté frontend (afficher inline).
 */
export function validateCustomFields(
  schema: FormSchemaDocument,
  rawValues: Record<string, unknown> | null | undefined,
):
  | { valid: true; values: Record<string, unknown> }
  | { valid: false; errors: FormValidationErrors } {
  const errors: FormValidationErrors = {};
  const values = rawValues ?? {};
  const out: Record<string, unknown> = {};

  for (const field of schema.fields) {
    // Champs non saisissables (séparateur, titre) : ignorer.
    if (field.type === 'section_title' || field.type === 'separator') continue;

    // Visibilité conditionnelle : si non visible, ne pas valider (mais
    // garder l'éventuelle valeur transmise pour ne rien perdre).
    if (field.visible_if) {
      const referenced = values[field.visible_if.field];
      if (!deepEqual(referenced, field.visible_if.equals)) {
        if (field.key in values) out[field.key] = values[field.key];
        continue;
      }
    }

    const raw = values[field.key];
    const usingDefault =
      (raw === undefined || raw === null || raw === '') && field.default !== undefined;
    const candidate = usingDefault ? field.default : raw;

    // Obligatoire
    if (field.required && (candidate === undefined || candidate === null || candidate === '')) {
      pushError(errors, field.key, 'Champ obligatoire');
      continue;
    }
    if (candidate === undefined || candidate === null || candidate === '') {
      continue; // Optionnel et vide → on n'inscrit rien
    }

    const fieldResult = validateFieldValue(field, candidate);
    if (!fieldResult.ok) {
      for (const msg of fieldResult.errors) pushError(errors, field.key, msg);
    } else {
      out[field.key] = fieldResult.value;
    }
  }

  if (Object.keys(errors).length > 0) {
    return { valid: false, errors };
  }
  return { valid: true, values: out };
}

function validateFieldValue(
  field: FormField,
  raw: unknown,
): { ok: true; value: unknown } | { ok: false; errors: string[] } {
  const errs: string[] = [];
  let coerced: unknown = raw;

  switch (field.type) {
    case 'text':
    case 'textarea': {
      const v = String(raw);
      coerced = v;
      const min = field.validation?.min;
      const max = field.validation?.max;
      if (min !== undefined && v.length < min) errs.push(`Min ${min} caractères`);
      if (max !== undefined && v.length > max) errs.push(`Max ${max} caractères`);
      if (field.validation?.regex && !new RegExp(field.validation.regex).test(v)) {
        errs.push('Format invalide');
      }
      break;
    }
    case 'number': {
      const n = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isNaN(n)) {
        errs.push('Doit être un nombre');
      } else {
        coerced = n;
        if (field.validation?.min !== undefined && n < field.validation.min) {
          errs.push(`Min ${field.validation.min}`);
        }
        if (field.validation?.max !== undefined && n > field.validation.max) {
          errs.push(`Max ${field.validation.max}`);
        }
      }
      break;
    }
    case 'boolean': {
      coerced = raw === true || raw === 'true' || raw === 1;
      break;
    }
    case 'date':
    case 'datetime': {
      const d = raw instanceof Date ? raw : new Date(String(raw));
      if (Number.isNaN(d.getTime())) errs.push('Date invalide');
      else coerced = d.toISOString();
      break;
    }
    case 'email': {
      const v = String(raw);
      coerced = v;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) errs.push('Email invalide');
      break;
    }
    case 'phone': {
      const v = String(raw);
      coerced = v;
      if (!/^[+\d\s\-.()]{6,30}$/.test(v)) errs.push('Numéro de téléphone invalide');
      break;
    }
    case 'url': {
      const v = String(raw);
      coerced = v;
      // Regex simple : http(s):// suivi d'un host valide. Évite la dépendance
      // au global `URL` (pas typé sans `lib: DOM` ou `@types/node`).
      if (!/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(v)) errs.push('URL invalide');
      break;
    }
    case 'select':
    case 'radio': {
      const v = String(raw);
      coerced = v;
      const allowed = (field.options ?? []).map((o) => o.value);
      if (allowed.length > 0 && !allowed.includes(v)) {
        errs.push('Valeur non autorisée');
      }
      break;
    }
    case 'multiselect': {
      const arr = Array.isArray(raw) ? raw.map((x) => String(x)) : [String(raw)];
      coerced = arr;
      const allowed = (field.options ?? []).map((o) => o.value);
      if (allowed.length > 0) {
        for (const v of arr) {
          if (!allowed.includes(v)) {
            errs.push(`Valeur non autorisée: ${v}`);
            break;
          }
        }
      }
      break;
    }
    case 'json': {
      coerced = raw;
      break;
    }
    case 'file': {
      // Le file upload réel est géré côté route (multipart) — ici on accepte
      // une référence (URL/key S3) comme string.
      coerced = String(raw);
      break;
    }
    default:
      coerced = raw;
  }

  if (errs.length > 0) return { ok: false, errors: errs };
  return { ok: true, value: coerced };
}

function pushError(errors: FormValidationErrors, key: string, msg: string) {
  if (!errors[key]) errors[key] = [];
  errors[key].push(msg);
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a && b && typeof a === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}
